/**
 * Tool definitions for PPTX XML-based editing capabilities.
 *
 * These tools let the AI unpack a PPTX binary, read/edit slide XML directly,
 * perform structural operations (duplicate/delete/reorder slides), and repack.
 */

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

export const UNPACK_PRESENTATION_TOOL: Tool = {
  name: 'unpack_presentation',
  description: 'Unpack a PPTX file for XML-based editing. Returns the slide list with indices, names, and dimensions.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation to unpack',
      },
    },
    required: ['presentationId'],
  },
};

export const READ_SLIDE_XML_TOOL: Tool = {
  name: 'read_slide_xml',
  description: 'Read a single slide\'s full XML content. Returns pretty-printed XML. Custom geometry (<a:custGeom>) is stripped by default to reduce size.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      slideIndex: {
        type: 'number',
        description: '0-based slide index',
      },
      stripGeometry: {
        type: 'boolean',
        description: 'Strip custom geometry path data to reduce XML size (default: true)',
      },
    },
    required: ['presentationId', 'slideIndex'],
  },
};

export const EDIT_SLIDE_XML_TOOL: Tool = {
  name: 'edit_slide_xml',
  description: 'Replace a slide\'s entire XML content. The XML must be valid and well-formed.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      slideIndex: {
        type: 'number',
        description: '0-based slide index',
      },
      newXml: {
        type: 'string',
        description: 'Complete replacement XML for the slide',
      },
    },
    required: ['presentationId', 'slideIndex', 'newXml'],
  },
};

export const PATCH_SLIDE_XML_TOOL: Tool = {
  name: 'patch_slide_xml',
  description: 'Apply targeted search/replace edits to a slide\'s XML. Returns editsApplied and editsFailed counts. Edits are applied sequentially.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      slideIndex: {
        type: 'number',
        description: '0-based slide index',
      },
      edits: {
        type: 'array',
        description: 'Ordered search/replace pairs applied sequentially',
        items: {
          type: 'object',
          properties: {
            search: { type: 'string', description: 'Exact substring to find in the XML' },
            replace: { type: 'string', description: 'Replacement string' },
            occurrence: { type: 'number', description: '1-based occurrence index to target the Nth match when search appears multiple times' },
          },
          required: ['search', 'replace'],
        },
      },
    },
    required: ['presentationId', 'slideIndex', 'edits'],
  },
};

export const DUPLICATE_SLIDE_TOOL: Tool = {
  name: 'duplicate_slide',
  description: 'Duplicate one or more slides. Copies are appended at the end in order.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      sourceSlideIndex: {
        type: 'number',
        description: '0-based index of a single slide to duplicate',
      },
      sourceSlideIndices: {
        type: 'array',
        items: { type: 'number' },
        description: '0-based indices of multiple slides to duplicate (batch)',
      },
    },
    required: ['presentationId'],
  },
};

export const DUPLICATE_PRESENTATION_TOOL: Tool = {
  name: 'duplicate_presentation',
  description: 'Create a complete copy of a presentation. The original remains untouched.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'Content ID of the presentation to duplicate.',
      },
    },
    required: ['presentationId'],
  },
};

export const DELETE_SLIDE_TOOL: Tool = {
  name: 'delete_slide',
  description: 'Delete slides. Use slideIndices array to delete multiple in one call. Cannot delete all slides.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      slideIndex: {
        type: 'number',
        description: '0-based index of a single slide to delete',
      },
      slideIndices: {
        type: 'array',
        items: { type: 'number' },
        description: '0-based indices of multiple slides to delete (batch)',
      },
    },
    required: ['presentationId'],
  },
};

export const REORDER_SLIDES_TOOL: Tool = {
  name: 'reorder_slides',
  description: 'Change the order of slides. Example: [2, 0, 1] moves current slide 2 to first, slide 0 to second, slide 1 to third.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      newOrder: {
        type: 'array',
        items: { type: 'number' },
        description: 'Array of current slide indices in desired order. Must contain each index exactly once.',
      },
    },
    required: ['presentationId', 'newOrder'],
  },
};

export const READ_PRESENTATION_IMAGE_TOOL: Tool = {
  name: 'read_presentation_image',
  description: 'Read an embedded image from a presentation. Returns the image for visual analysis.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      imageName: {
        type: 'string',
        description: 'Image filename (e.g. "image1.png")',
      },
    },
    required: ['presentationId', 'imageName'],
  },
};

export const EXTRACT_SLIDE_TEXT_TOOL: Tool = {
  name: 'extract_slide_text',
  description: 'Extract a structured text map from one or all slides. Returns every text element with shape name, position, font size, group membership, occurrence numbers for repeated texts, and individual run contents for multi-run texts.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation',
      },
      slideIndex: {
        type: 'number',
        description: '0-based slide index. Omit to extract from all slides.',
      },
    },
    required: ['presentationId'],
  },
};

export const REPACK_PRESENTATION_TOOL: Tool = {
  name: 'repack_presentation',
  description: 'Clean orphaned files, condense XML, and repack into a PPTX binary. Updates the preview panel.',
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'ID of the presentation to repack',
      },
    },
    required: ['presentationId'],
  },
};
